import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';

import type {SettingsStackParamList} from '@/app/routes';
import {
  deleteProfile,
  fetchDevice,
  fetchDevices,
  fetchProfiles,
  saveProfile,
} from '@/entities/device/api';
import type {SettingsProfile} from '@/entities/device/types';
import {useAuthStore} from '@/entities/session/auth-store';
import {ContextPlaceholder} from '@/widgets/context-placeholder/ContextPlaceholder';
import {ApiError} from '@/shared/api';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {EmptyState} from '@/shared/ui/empty-state';
import {AppInput} from '@/shared/ui/input';
import {ListRow, PagedRows} from '@/shared/ui/list-row';
import {Page} from '@/shared/ui/page';
import {SettingsNav} from '@/pages/settings/SettingsNav';
import {Section} from '@/shared/ui/section';
import {ChoiceChips} from '@/shared/ui/segmented';
import {Sheet} from '@/shared/ui/sheet';
import {Skeleton} from '@/shared/ui/skeleton';
import {StatusPill} from '@/shared/ui/status';
import {AppText} from '@/shared/ui/text';
import {Toggle} from '@/shared/ui/toggle';
import {radii, spacing} from '@/shared/ui/theme';

/**
 * Settings profiles — one named set of terminal settings, assigned to any number of
 * devices, so a company with twelve doors configures them once.
 *
 * ## Why creating one starts from a device
 *
 * A profile's payload is the full 23-field `TerminalSettings` object: the server takes
 * nothing partial, and a profile created from hand-picked defaults would quietly differ
 * from what the terminals in this company actually run (admin password, company name, logo,
 * language, camera source — all company-specific). So a new profile is seeded from an
 * existing DEVICE's effective settings, which are by definition a working configuration
 * for this company's hardware. Renaming and re-assigning happen here; changing the
 * settings themselves stays where it already is — on the device settings screen, which
 * has the full form and its explanations.
 *
 * `is_global_default` is a company-wide switch, not a per-profile flag: turning it on
 * for one profile is what every device with no explicit assignment then resolves to.
 */
type Props = NativeStackScreenProps<SettingsStackParamList, 'TerminalProfiles'>;

const styles = StyleSheet.create({
  /** Подпись, пояснение и сам выбор — одним блоком внутри листа. */
  field: {gap: spacing(2)},
  form: {gap: spacing(3)},
});

export function TerminalProfilesScreen(_: Props) {
  const {t} = useTranslation();
  const qc = useQueryClient();
  const companyId = useAuthStore(s => s.activeContext?.companyId);

  const profiles = useQuery({
    queryKey: ['profiles', companyId],
    queryFn: () => fetchProfiles(companyId as string),
    enabled: !!companyId,
  });

  const devices = useQuery({
    queryKey: ['devices', companyId, 'all'],
    queryFn: () => fetchDevices(companyId as string, {perPage: 100}),
    enabled: !!companyId,
  });

  const [editing, setEditing] = useState<SettingsProfile | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [sourceDeviceId, setSourceDeviceId] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const closeSheet = () => {
    setEditing(null);
    setCreating(false);
    setName('');
    setDescription('');
    setIsDefault(false);
    setSourceDeviceId('');
    save.reset();
  };

  const save = useMutation({
    meta: {toast: 'toast.profileSaved'},
    mutationFn: async () => {
      if (editing) {
        // Rename/redescribe only: the payload is round-tripped unchanged, or the save
        // would replace a working configuration with whatever this screen happened to
        // have in memory.
        return saveProfile(companyId as string, {
          profile_id: editing.profile_id,
          name: name.trim(),
          description: description.trim(),
          payload: editing.payload,
          is_global_default: isDefault,
        });
      }
      const source = await fetchDevice(companyId as string, sourceDeviceId);
      return saveProfile(companyId as string, {
        name: name.trim(),
        description: description.trim(),
        payload: source.effective_settings,
        is_global_default: isDefault,
      });
    },
    onSuccess: async () => {
      closeSheet();
      await qc.invalidateQueries({queryKey: ['profiles', companyId]});
    },
  });

  const remove = useMutation({
    meta: {toast: 'toast.profileDeleted'},
    mutationFn: (profileId: string) => deleteProfile(companyId as string, profileId),
    onSuccess: async () => {
      setConfirmDelete(null);
      await qc.invalidateQueries({queryKey: ['profiles', companyId]});
    },
  });

  if (!companyId) {
    return <ContextPlaceholder titleKey="more.terminalProfiles" />;
  }

  const list = profiles.data ?? [];
  const deviceOptions = (devices.data?.data ?? []).map(d => ({
    value: d.device_id,
    label: d.device_name,
  }));

  const canSave =
    name.trim().length > 0 && (editing !== null || sourceDeviceId.length > 0);

  return (
    <Page
      sideNav={<SettingsNav />}
      width="form"
      scroll
      testID="terminal-profiles"
      title={t('more.terminalProfiles')}
      subtitle={t('profiles.intro')}
      >

      <Section
        title={t('profiles.list')}
        actionLabel={deviceOptions.length > 0 ? t('profiles.create') : undefined}
        onAction={
          deviceOptions.length > 0
            ? () => {
                closeSheet();
                setCreating(true);
              }
            : undefined
        }>
        {profiles.isPending ? (
          <Skeleton height={140} radius={radii.lg} />
        ) : profiles.isError ? (
          <Banner tone="error" title={t('errors.generic')} />
        ) : list.length === 0 ? (
          <EmptyState
            title={t('profiles.empty')}
            hint={
              deviceOptions.length === 0
                ? t('profiles.emptyNoDevices')
                : t('profiles.emptyHint')
            }
          />
        ) : (
          <PagedRows
            items={list}
            keyOf={profile => profile.profile_id}
            testID="profiles-list"
            render={profile => (
              <ListRow
                icon="settings"
                title={profile.name}
                subtitle={
                  profile.description ||
                  t('profiles.deviceCount', {count: profile.device_count})
                }
                trailing={
                  profile.is_global_default ? (
                    <StatusPill
                      tone="granted"
                      size="sm"
                      label={t('profiles.default')}
                    />
                  ) : undefined
                }
                onPress={() => {
                  setCreating(false);
                  setEditing(profile);
                  setName(profile.name);
                  setDescription(profile.description);
                  setIsDefault(profile.is_global_default);
                }}
                testID={`profile-${profile.profile_id}`}
              />
            )}
          />
        )}
      </Section>

      <Sheet
        visible={creating || editing !== null}
        onClose={closeSheet}
        title={t(editing ? 'profiles.edit' : 'profiles.create')}
        subtitle={t(editing ? 'profiles.editHint' : 'profiles.createHint')}
        testID="profile-sheet">
        <View style={styles.form}>
          <AppInput
            label={t('profiles.name')}
            value={name}
            onChangeText={setName}
            testID="profile-name"
          />
          <AppInput
            label={t('profiles.description')}
            value={description}
            onChangeText={setDescription}
            testID="profile-description"
          />

          {!editing ? (
            <View style={styles.field}>
              <AppText variant="label">{t('profiles.copyFrom')}</AppText>
              <AppText variant="caption">{t('profiles.copyFromHint')}</AppText>
              <ChoiceChips
                value={sourceDeviceId}
                onChange={setSourceDeviceId}
                options={deviceOptions}
                testID="profile-source-device"
              />
            </View>
          ) : null}

          <Card padding={3} variant="flat">
            <Toggle
              label={t('profiles.default')}
              hint={t('profiles.defaultHint')}
              value={isDefault}
              onChange={setIsDefault}
            />
          </Card>

          {save.isError ? (
            <Banner
              tone="error"
              title={
                save.error instanceof ApiError
                  ? save.error.message
                  : t('errors.generic')
              }
            />
          ) : null}

          <AppButton
            title={t('actions.save')}
            disabled={!canSave}
            loading={save.isPending}
            onPress={() => save.mutate()}
            testID="profile-save"
          />

          {editing ? (
            confirmDelete === editing.profile_id ? (
              <>
                <Banner
                  tone="warning"
                  title={t('profiles.deleteWarning')}
                  message={t('profiles.deleteScope')}
                />
                <AppButton
                  title={t('profiles.deleteDo')}
                  tone="destructive"
                  loading={remove.isPending}
                  onPress={() => remove.mutate(editing.profile_id)}
                  testID="profile-delete-confirm"
                />
                <AppButton
                  title={t('actions.cancel')}
                  tone="ghost"
                  onPress={() => setConfirmDelete(null)}
                />
              </>
            ) : (
              <AppButton
                title={t('profiles.delete')}
                tone="ghost"
                onPress={() => setConfirmDelete(editing.profile_id)}
                testID="profile-delete"
              />
            )
          ) : null}
        </View>
      </Sheet>
    </Page>
  );
}
